import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { MonitoringSite } from '../../types/apiModels'
import { deleteMonitoringSite as deleteMonitoringSiteService } from '../../services/farmMapping/monitoringSites/deleteMonitoringSite'
import getMonitoringOnly from '../../services/farmMapping/monitoringSites/getMonitoringOnly'
import getMonitoringSites from '../../services/farmMapping/monitoringSites/getMonitoringSites'
import addMonitoringSiteV2 from '../../services/ruutsApi/v2/monitoring/sites/add'
import renameMonitoringSiteV2 from '../../services/ruutsApi/v2/monitoring/sites/rename'
import { queryKeys } from './queryKeys'
import { invalidateTasksAndActivities } from './useMonitoringEvents'

export const monitoringSitesQueryOptions = (
  farmId: string | undefined,
  { monitoringOnly = false }: { monitoringOnly?: boolean } = {},
) =>
  queryOptions({
    queryKey: queryKeys.monitoringSites(farmId, { monitoringOnly }),
    queryFn: ({ signal }): Promise<MonitoringSite[]> =>
      monitoringOnly
        ? getMonitoringOnly({ farmId: farmId as string, signal })
        : getMonitoringSites({ farmId: farmId as string, signal }),
  })

export const useMonitoringSites = ({
  farmId,
  isMonitoringOnly = false,
}: {
  farmId?: string
  isMonitoringOnly?: boolean
}) =>
  useQuery({
    ...monitoringSitesQueryOptions(farmId, { monitoringOnly: isMonitoringOnly }),
    enabled: !!farmId,
  })

// Both options exist for the bulk fan-out and default to off, so a lone add keeps the ordinary
// behaviour: suppressGlobalError because that caller reports every rejection itself (naming the
// site, and the conflicting one on a 409), and skipInvalidation because invalidating per site
// would refetch the list while the rest of the batch is still in flight.
export const useAddMonitoringSite = ({
  farmId,
  skipInvalidation = false,
  suppressGlobalError = false,
}: {
  farmId: string | undefined
  skipInvalidation?: boolean
  suppressGlobalError?: boolean
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ site }: { site: MonitoringSite }) => addMonitoringSiteV2({ site }),
    meta: { suppressGlobalError },
    onSuccess: () => {
      if (skipInvalidation) return
      queryClient.invalidateQueries({ queryKey: queryKeys.monitoringSites(farmId) })
    },
  })
}

export const useRenameMonitoringSite = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ siteId, name }: { siteId: string; name: string }) =>
      renameMonitoringSiteV2({ siteId, name }),
    // The Perímetro rename reports every rejection itself, naming the conflicting site on a 409.
    meta: { suppressGlobalError: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.monitoringSites(farmId) })
    },
  })
}

export const useDeleteMonitoringSite = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ siteId }: { siteId: string }) => deleteMonitoringSiteService({ siteId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.monitoringSites(farmId) })
      invalidateTasksAndActivities(queryClient)
    },
  })
}
